import { queryKeys } from '../keys'
import { DEFAULT_RETRY_MUTATION } from '../config'
import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import type { GetMyBookingsResponse } from '@/types/my-booking'
import { myBookingService } from '@/services/my-booking'
import { BOOKING_STATUS } from '@/utils/constant'

function deleteBooking(queryClient: QueryClient) {
  return queryClient.setMutationDefaults(queryKeys.myBooking.delete(), {
    retry: DEFAULT_RETRY_MUTATION,
    mutationFn: (payload: Parameters<typeof myBookingService.delete>[0]) =>
      myBookingService.delete(payload),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.myBooking.list({
          status: BOOKING_STATUS.DRAFT,
        }),
      })

      const infiniteBookings = queryClient.getQueryData<
        InfiniteData<GetMyBookingsResponse>
      >(
        queryKeys.myBooking.list({
          status: BOOKING_STATUS.DRAFT,
        }),
      )
      const bookings =
        infiniteBookings?.pages?.flatMap((p) => p.data.bookings || []) || []

      const removedBooking = bookings.some(
        (booking) => booking.id === payload.id,
      )

      if (!removedBooking) return

      queryClient.setQueryData<InfiniteData<GetMyBookingsResponse>>(
        queryKeys.myBooking.list({
          status: BOOKING_STATUS.DRAFT,
        }),
        (oldData) =>
          !oldData
            ? oldData
            : {
                ...oldData,
                pages: oldData.pages.map((page) => ({
                  ...page,
                  data: {
                    ...page.data,
                    bookings: page.data.bookings?.filter(
                      (booking) => booking.id !== payload.id,
                    ),
                  },
                })),
              },
      )

      return { infiniteBookings }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.myBooking.tabList(),
      })
    },
    onError: (_err, _, ctx) => {
      if (!ctx) return
      queryClient.setQueryData<InfiniteData<GetMyBookingsResponse>>(
        queryKeys.myBooking.list({
          status: BOOKING_STATUS.DRAFT,
        }),
        ctx.infiniteBookings,
      )
    },
  })
}

export function registerMyBookingMutations(queryClient: QueryClient) {
  deleteBooking(queryClient)
}
